import {HttpClient} from '@angular/common/http';
import {Injectable} from '@angular/core';
import {BehaviorSubject, catchError, filter, map, Observable, of, tap} from 'rxjs';
import {environment} from "../../../environment/environment";
import {ToastService} from "./toast.service";
import {Router} from "@angular/router";
import {UserRepository} from "../../modules/user/states/user.repository";
import {AnimalSheltersRepository} from "../states/animal-shelter.repository";
import {WaitingDialogService} from './waiting-dialog.service';
import {TokenService} from './token.service';
import {RestService} from "./rest.service";
import {User} from "../../modules/user/models/user.model";

@Injectable({
  providedIn: 'root'
})
export class AuthService {
  public isLoggedInSubject = new BehaviorSubject<boolean>(false);
  public isLoggedIn$ = this.isLoggedInSubject.asObservable();
  private readonly _currentUserSubject = new BehaviorSubject<User | null>(null);
  currentUser$ = this._currentUserSubject.asObservable().pipe(filter(user => user !== null));
  loggedInUser: User | null = null;

  constructor(
    private readonly _router: Router,
    private readonly _http: HttpClient,
    private readonly _toastService: ToastService,
    private readonly _usersRepository: UserRepository,
    private readonly _animalSheltersRepository: AnimalSheltersRepository,
    private readonly _waitingDialogService: WaitingDialogService,
    private readonly _tokenService: TokenService,
    private readonly _restService: RestService
  ) {
    this.loadToken();
  }

  login(password: string): Observable<boolean> {
    return this._http
      .post<{ token: string }>(environment.apiUrl + '/login', {"pin": password})
      .pipe(
        tap(response => {
          this._tokenService.setTokens(response);
          this.loadUser();

          this.isLoggedInSubject.next(true);
          this._toastService.showSuccess('Login erfolgreich');
          this._router.navigate(['/']);
        }),
        map(() => true),
        catchError(() => {
          this.isLoggedInSubject.next(false);
          this._toastService.showError('Login fehlgeschlagen');

          return of(false);
        }),
        tap(() => this._waitingDialogService.showWaitingDialog(false))
      );
  }

  loadUser() {
    this._restService.httpGet('/users/me').subscribe((user: any) => {
      if (!user) {
        return;
      }
      this.loggedInUser = user;
      this._usersRepository.removeAllUsers();
      this._usersRepository.addUser({
        id: user.id,
        name: user.displayName,
        token: user.token,
        roles: user.roles,
        defaultAnimalShelter: user.defaultAnimalShelter
      });

      this._usersRepository.setActiveUserId(user.id);
      this._currentUserSubject.next(this.loggedInUser);
      this._animalSheltersRepository.setActiveAnimalShelter(user.defaultAnimalShelter);
    });
  }

  loadToken() {
    const token = this._tokenService.getAccessToken();
    if (token) {
      this.isLoggedInSubject.next(true);
    } else {
      this.isLoggedInSubject.next(false);
    }
  }

  logout() {
    this._tokenService.clearToken();
    this.isLoggedInSubject.next(false);
    this._router.navigate(['/login']);
  }
}
